import CloseIcon from '@material-ui/icons/Close'
import { useEffect, useState } from 'react'
import { Relative, Wrap } from 'shared/components/Base/Base'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { DropZoneArea } from 'shared/components/DropZoneArea/DropZoneArea'
import { LeftPanelClose, Trash } from 'shared/components/Icons/Icon'
import { Text } from 'shared/components/Text/Text'
import { Handler, HandlerOf } from 'shared/helpers/typeHelper'
import { useBreakpoint } from 'shared/hooks/useBreakpoint'
import {
  Attachment,
  CoreDocumentAttachment,
  isAttachment,
  isCoreDocumentAttachment,
} from 'shared/models/document'
import { breakpoints } from 'shared/theme/breakpoints'
import { black, blueGray, midGray, primaryBlack, royalBlue, white } from 'shared/theme/colors'
import styled, { css } from 'styled-components'

interface FileUploadWithSlideoutPreviewProps {
  file: File | Attachment | CoreDocumentAttachment<any> | undefined
  setFile: HandlerOf<File | undefined>
  positionExpandedUnderToolbar?: boolean
  label?: React.ReactNode
  labelAtBottom?: React.ReactNode
  showTrashIcon?: boolean
  isReadOnly?: boolean
}

const urlCache = new Map()

function getNameFromFile(file: File | Attachment | CoreDocumentAttachment<any>) {
  if (isCoreDocumentAttachment(file)) {
    return file.originalFilename
  }

  if (isAttachment(file)) {
    return file.original_filename
  }

  return file.name
}

function getUrlFromFile(file: File | Attachment | CoreDocumentAttachment<any>) {
  if (isCoreDocumentAttachment(file)) return file.attachmentFile

  if (isAttachment(file)) return file.file ? file.file : file.attachment_file

  if (urlCache.has(file)) {
    return urlCache.get(file)
  }

  urlCache.set(file, URL.createObjectURL(file))
  return urlCache.get(file)
}

// https://stackoverflow.com/questions/10253669/how-to-get-the-number-of-pages-of-a-pdf-uploaded-by-user
function getPdfPages(file: File | Attachment, cb: HandlerOf<number | null>) {
  if (isAttachment(file)) return

  const reader = new FileReader()
  reader.readAsBinaryString(file)
  reader.onloadend = () => {
    if (typeof reader.result === 'string') {
      const count = reader.result?.match(/\/Type[\s]*\/Page[^s]/g)?.length
      cb(count ?? null)
    }
  }
}

export function FileUploadWithSlideoutPreview({
  file,
  setFile,
  label,
  labelAtBottom,
  positionExpandedUnderToolbar = false,
  showTrashIcon = true,
  isReadOnly,
}: FileUploadWithSlideoutPreviewProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [pages, setPages] = useState<number | null>(null)
  const isMobile = useBreakpoint('sm')

  useEffect(() => {
    if (!file || isCoreDocumentAttachment(file) || isAttachment(file)) {
      setPages(null)
    } else if (file && file instanceof File && file.type.startsWith('application/pdf')) {
      getPdfPages(file, setPages)
    } else {
      setPages(null)
    }
  }, [file])

  function renderContent() {
    if (!file)
      return (
        <>
          <StyledDropZoneArea initialFile={file} onChange={setFile} />
          {label && labelAtBottom && !isMobile && (
            <Text variant="label3" mb={2}>
              {label}
            </Text>
          )}
        </>
      )

    if (isMobile) {
      return (
        <Relative>
          <StyledDropZoneArea
            initialFile={isCoreDocumentAttachment(file) || isAttachment(file) ? undefined : file}
            uploadedFile={
              isCoreDocumentAttachment(file)
                ? file.attachmentFile
                : isAttachment(file)
                ? file.file
                  ? file.file
                  : file.attachment_file
                : undefined
            }
            onChange={setFile}
            isDisabled={isReadOnly}
          />
          <MobilePreview onClick={() => setIsExpanded(true)} />
        </Relative>
      )
    }

    return (
      <>
        <Preview onClick={() => setIsExpanded(true)}>
          <PreviewLabel>
            <FlexBox alignItems="center" gap="15px">
              <Text variant="label2" fontColor={white} mt={0.4}>
                Slideout view
              </Text>
              <img src={LeftPanelClose} alt="" />
            </FlexBox>
          </PreviewLabel>
          {renderFile(file)}
        </Preview>
        {label && labelAtBottom && (
          <Text variant="label3" mb={1}>
            {label}
          </Text>
        )}
        <FlexBox justifyContent="space-between" mt={1} gap="15px" alignItems="flex-start">
          <Text variant="label3" fontColor={midGray}>
            {getNameFromFile(file)}
          </Text>
          {showTrashIcon && !isReadOnly && (
            <FlexBox
              gap="5px"
              alignItems="center"
              mt={-0.25 /* For delete text to align with trash icon */}
              styles="cursor: pointer"
              onClick={() => setFile(undefined)}
            >
              <TrashIcon src={Trash} alt="delete" />
              <Text
                variant="header4"
                fontColor={royalBlue}
                mt={0.25 /*baseline of the row is a little low */}
              >
                Delete
              </Text>
            </FlexBox>
          )}
        </FlexBox>
      </>
    )
  }

  function renderFile(file: File | Attachment | CoreDocumentAttachment<any>) {
    const type = isCoreDocumentAttachment(file) || isAttachment(file) ? file.mimetype : file.type

    if (type.startsWith('image')) {
      return <ImagePreview src={getUrlFromFile(file)} alt="" />
    }

    if (type.startsWith('application/pdf')) {
      return <PdfPreview src={`${getUrlFromFile(file)}#toolbar=0&navpanes=0`} title="" />
    }

    return (
      <Text variant="label4" fontColor={midGray}>
        Cannot preview this file
      </Text>
    )
  }

  function renderExpanded() {
    if (!isExpanded || !file) return null
    return (
      <Expanded
        file={file}
        onClose={() => setIsExpanded(false)}
        pages={pages}
        renderFile={renderFile}
        positionExpandedUnderToolbar={positionExpandedUnderToolbar}
      />
    )
  }

  return (
    <OuterWrap>
      {label && (!labelAtBottom || isMobile) && (
        <Text variant="label3" mb={1}>
          {label}
        </Text>
      )}
      {renderContent()}
      {renderExpanded()}
    </OuterWrap>
  )
}

interface ExpandedProps {
  file: File | Attachment | CoreDocumentAttachment<any>
  pages: number | null
  onClose: Handler
  positionExpandedUnderToolbar: boolean
  renderFile: (file: File | Attachment | CoreDocumentAttachment<any>) => React.ReactNode
}

function Expanded({ file, pages, renderFile, onClose, positionExpandedUnderToolbar = false }: ExpandedProps) {
  const [transitionIn, setTransitionIn] = useState(false)

  useEffect(() => {
    setTimeout(() => setTransitionIn(true), 10)
  }, [])

  function handleClose() {
    setTransitionIn(false)
    setTimeout(onClose, 500)
  }

  return (
    <ExpandedWrap $transitionIn={transitionIn} $positionExpandedUnderToolbar={positionExpandedUnderToolbar}>
      <ExpandedToolbar>
        <StyledCloseIcon onClick={handleClose} />
        <Text variant="label4" fontColor={black} mt={0.5}>
          {getNameFromFile(file)}
        </Text>
        {pages && (
          <Text variant="label3" fontColor={midGray}>
            {pages} page{pages === 1 ? '' : 's'}
          </Text>
        )}
      </ExpandedToolbar>
      <ExpandedFileWrap>{renderFile(file)}</ExpandedFileWrap>
    </ExpandedWrap>
  )
}

const OuterWrap = styled(Wrap)`
  width: 100%;

  @media ${breakpoints.sm} {
    grid-column: 1;
    grid-row: 1;
    margin: 0;
  }
`

const GrayText = styled.span`
  color: ${midGray};
`

const StyledDropZoneArea = styled(DropZoneArea)`
  @media ${breakpoints.mdUp} {
    width: 100%;
    max-width: none;
    height: 373px;

    ${GrayText} {
      &::before {
        content: '';
        display: block;
      }
    }
  }
`

const ImagePreview = styled.img`
  width: 100%;
  max-width: 100%;
`

const PdfPreview = styled.iframe`
  width: 100%;
  max-width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: none;
`

const TrashIcon = styled.img`
  cursor: pointer;
`

const PreviewLabel = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0;
  transition: opacity 0.3s;
  color: white;
  z-index: 2;
`

const Preview = styled.div<{ $isExpanded?: boolean }>`
  cursor: pointer;
  position: relative;
  min-height: 300px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${primaryBlack};
    opacity: 0;
    transition: opacity 0.3s;
    z-index: 2;
  }

  &:hover ${PreviewLabel} {
    opacity: 1;
  }

  &:hover::before {
    opacity: 0.6;
  }

  ${(p) =>
    p.$isExpanded &&
    css`
      &:before {
        opacity: 0.6;
      }
    `}
`

const MobilePreview = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 60px;
  cursor: pointer;
`

const ExpandedWrap = styled.div<{ $transitionIn?: boolean; $positionExpandedUnderToolbar?: boolean }>`
  position: fixed;
  z-index: 10;
  top: 0;
  right: 0;
  bottom: 0;
  width: calc(50vw - 80px);
  height: 100vh;
  transform: translateX(100%);
  transition: transform 0.3s;
  display: flex;
  flex-direction: column;
  overflow: auto;

  ${(p) =>
    p.$transitionIn &&
    css`
      transform: translateX(0);
    `}

  ${(p) =>
    p.$positionExpandedUnderToolbar &&
    css`
      top: 48px;
      height: calc(100vh - 48px);

      header[style*='translateY(-48px)'] + main & {
        top: 0;
        height: 100vh;
      }
    `}

  @media ${breakpoints.sm} {
    width: 100vw;
  }
`

const ExpandedFileWrap = styled.div`
  position: relative;
  background: white;
  flex: 1;
`

const ExpandedToolbar = styled.div`
  padding: 10px;
  display: grid;
  grid-template-columns: 20px 1fr auto;
  align-items: center;
  gap: 10px;
  background: white;
`

const StyledCloseIcon = styled(CloseIcon)`
  color: ${blueGray};
  cursor: pointer;
`
